/**
 * Single source of truth for country metadata used across the app.
 * Avoids the duplicated COUNTRY_FLAG maps that existed in 5 different
 * files before this refactor.
 *
 * Ordering: KeenEZ's primary HR/payroll jurisdictions (JP/US/GB/DE)
 * sit at the top so they're one click away in the picker; the rest
 * follow alphabetically by English name. Country codes are ISO
 * 3166-1 alpha-2.
 */
export interface CountryMeta {
  code: string;
  flag: string;
  /** ISO short name in English. Localized labels are in i18n. */
  englishName: string;
}

export const COUNTRIES: CountryMeta[] = [
  // --- pinned (KeenEZ priority jurisdictions) ---
  { code: 'JP', flag: '🇯🇵', englishName: 'Japan' },
  { code: 'US', flag: '🇺🇸', englishName: 'United States' },
  { code: 'GB', flag: '🇬🇧', englishName: 'United Kingdom' },
  { code: 'DE', flag: '🇩🇪', englishName: 'Germany' },

  // --- rest, alphabetical by English name ---
  { code: 'AR', flag: '🇦🇷', englishName: 'Argentina' },
  { code: 'AU', flag: '🇦🇺', englishName: 'Australia' },
  { code: 'AT', flag: '🇦🇹', englishName: 'Austria' },
  { code: 'BH', flag: '🇧🇭', englishName: 'Bahrain' },
  { code: 'BD', flag: '🇧🇩', englishName: 'Bangladesh' },
  { code: 'BE', flag: '🇧🇪', englishName: 'Belgium' },
  { code: 'BR', flag: '🇧🇷', englishName: 'Brazil' },
  { code: 'BG', flag: '🇧🇬', englishName: 'Bulgaria' },
  { code: 'KH', flag: '🇰🇭', englishName: 'Cambodia' },
  { code: 'CA', flag: '🇨🇦', englishName: 'Canada' },
  { code: 'CL', flag: '🇨🇱', englishName: 'Chile' },
  { code: 'CN', flag: '🇨🇳', englishName: 'China' },
  { code: 'CO', flag: '🇨🇴', englishName: 'Colombia' },
  { code: 'CR', flag: '🇨🇷', englishName: 'Costa Rica' },
  { code: 'HR', flag: '🇭🇷', englishName: 'Croatia' },
  { code: 'CY', flag: '🇨🇾', englishName: 'Cyprus' },
  { code: 'CZ', flag: '🇨🇿', englishName: 'Czechia' },
  { code: 'DK', flag: '🇩🇰', englishName: 'Denmark' },
  { code: 'DO', flag: '🇩🇴', englishName: 'Dominican Republic' },
  { code: 'EC', flag: '🇪🇨', englishName: 'Ecuador' },
  { code: 'EG', flag: '🇪🇬', englishName: 'Egypt' },
  { code: 'EE', flag: '🇪🇪', englishName: 'Estonia' },
  { code: 'FI', flag: '🇫🇮', englishName: 'Finland' },
  { code: 'FR', flag: '🇫🇷', englishName: 'France' },
  { code: 'GE', flag: '🇬🇪', englishName: 'Georgia' },
  { code: 'GH', flag: '🇬🇭', englishName: 'Ghana' },
  { code: 'GR', flag: '🇬🇷', englishName: 'Greece' },
  { code: 'GT', flag: '🇬🇹', englishName: 'Guatemala' },
  { code: 'HK', flag: '🇭🇰', englishName: 'Hong Kong' },
  { code: 'HU', flag: '🇭🇺', englishName: 'Hungary' },
  { code: 'IS', flag: '🇮🇸', englishName: 'Iceland' },
  { code: 'IN', flag: '🇮🇳', englishName: 'India' },
  { code: 'ID', flag: '🇮🇩', englishName: 'Indonesia' },
  { code: 'IE', flag: '🇮🇪', englishName: 'Ireland' },
  { code: 'IL', flag: '🇮🇱', englishName: 'Israel' },
  { code: 'IT', flag: '🇮🇹', englishName: 'Italy' },
  { code: 'JO', flag: '🇯🇴', englishName: 'Jordan' },
  { code: 'KZ', flag: '🇰🇿', englishName: 'Kazakhstan' },
  { code: 'KE', flag: '🇰🇪', englishName: 'Kenya' },
  { code: 'KR', flag: '🇰🇷', englishName: 'Korea, Republic of' },
  { code: 'KW', flag: '🇰🇼', englishName: 'Kuwait' },
  { code: 'LV', flag: '🇱🇻', englishName: 'Latvia' },
  { code: 'LB', flag: '🇱🇧', englishName: 'Lebanon' },
  { code: 'LT', flag: '🇱🇹', englishName: 'Lithuania' },
  { code: 'LU', flag: '🇱🇺', englishName: 'Luxembourg' },
  { code: 'MO', flag: '🇲🇴', englishName: 'Macao' },
  { code: 'MY', flag: '🇲🇾', englishName: 'Malaysia' },
  { code: 'MT', flag: '🇲🇹', englishName: 'Malta' },
  { code: 'MX', flag: '🇲🇽', englishName: 'Mexico' },
  { code: 'MA', flag: '🇲🇦', englishName: 'Morocco' },
  { code: 'NL', flag: '🇳🇱', englishName: 'Netherlands' },
  { code: 'NZ', flag: '🇳🇿', englishName: 'New Zealand' },
  { code: 'NG', flag: '🇳🇬', englishName: 'Nigeria' },
  { code: 'NO', flag: '🇳🇴', englishName: 'Norway' },
  { code: 'OM', flag: '🇴🇲', englishName: 'Oman' },
  { code: 'PK', flag: '🇵🇰', englishName: 'Pakistan' },
  { code: 'PA', flag: '🇵🇦', englishName: 'Panama' },
  { code: 'PY', flag: '🇵🇾', englishName: 'Paraguay' },
  { code: 'PE', flag: '🇵🇪', englishName: 'Peru' },
  { code: 'PH', flag: '🇵🇭', englishName: 'Philippines' },
  { code: 'PL', flag: '🇵🇱', englishName: 'Poland' },
  { code: 'PT', flag: '🇵🇹', englishName: 'Portugal' },
  { code: 'QA', flag: '🇶🇦', englishName: 'Qatar' },
  { code: 'RO', flag: '🇷🇴', englishName: 'Romania' },
  { code: 'RU', flag: '🇷🇺', englishName: 'Russian Federation' },
  { code: 'SA', flag: '🇸🇦', englishName: 'Saudi Arabia' },
  { code: 'RS', flag: '🇷🇸', englishName: 'Serbia' },
  { code: 'SG', flag: '🇸🇬', englishName: 'Singapore' },
  { code: 'SK', flag: '🇸🇰', englishName: 'Slovakia' },
  { code: 'SI', flag: '🇸🇮', englishName: 'Slovenia' },
  { code: 'ZA', flag: '🇿🇦', englishName: 'South Africa' },
  { code: 'ES', flag: '🇪🇸', englishName: 'Spain' },
  { code: 'LK', flag: '🇱🇰', englishName: 'Sri Lanka' },
  { code: 'SE', flag: '🇸🇪', englishName: 'Sweden' },
  { code: 'CH', flag: '🇨🇭', englishName: 'Switzerland' },
  { code: 'TW', flag: '🇹🇼', englishName: 'Taiwan' },
  { code: 'TZ', flag: '🇹🇿', englishName: 'Tanzania' },
  { code: 'TH', flag: '🇹🇭', englishName: 'Thailand' },
  { code: 'TR', flag: '🇹🇷', englishName: 'Türkiye' },
  { code: 'UA', flag: '🇺🇦', englishName: 'Ukraine' },
  { code: 'AE', flag: '🇦🇪', englishName: 'United Arab Emirates' },
  { code: 'UY', flag: '🇺🇾', englishName: 'Uruguay' },
  { code: 'UZ', flag: '🇺🇿', englishName: 'Uzbekistan' },
  { code: 'VN', flag: '🇻🇳', englishName: 'Vietnam' },
];

const BY_CODE: Map<string, CountryMeta> = new Map(COUNTRIES.map((c) => [c.code, c]));

export function countryByCode(code: string | null | undefined): CountryMeta | null {
  if (!code) return null;
  return BY_CODE.get(code.toUpperCase()) ?? null;
}

export function countryFlag(code: string | null | undefined): string {
  return countryByCode(code)?.flag ?? '🏳';
}

export function countryLabel(code: string | null | undefined): string {
  const meta = countryByCode(code);
  if (!meta) return code ?? '';
  return `${meta.flag} ${meta.englishName}`;
}
